import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface VentureCategory {
  id: string;
  coverImage: string;
}

const ventureCategories: VentureCategory[] = [
  {
    id: 'metal',
    coverImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/f70d1e93c788fdabf987c14c4087ebff.jpeg',
  },
  {
    id: 'fujifilm',
    coverImage: 'https://static.readdy.ai/image/37826dbbd11b57a8921406d974f7aedc/016a48ddb4fbcc2be7e8bdd854be9589.jpeg',
  },
  {
    id: 'fashion',
    coverImage: 'https://storage.readdy-site.link/project_files/e7a178d6-0560-413e-a55b-8af9dbae3f7b/a212845c-9a4f-4d1a-bd9f-ad3b754ed96c__20260404000200_104_51.jpg?v=3c81c369f091abfd2fb7114f974658e5',
  },
];

interface CategoryCardProps {
  category: VentureCategory;
  index: number;
  isVisible: boolean;
}

function CategoryCard({ category, index, isVisible }: CategoryCardProps) {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  const greenAccent = '#3a7a52';
  const greenBorder = 'rgba(58,122,82,0.5)';
  const greenBorderDim = 'rgba(58,122,82,0.25)';
  const greenGlow = 'rgba(58,122,82,0.3)';
  const greenGlowSoft = 'rgba(58,122,82,0.12)';
  const overlayColor = 'rgba(58,122,82,0.04)';
  const overlayColorBottom = 'rgba(58,122,82,0.06)';

  const handleClick = () => {
    navigate(`/gallery/ventures/${category.id}`);
  };

  return (
    <div
      className={`relative overflow-hidden cursor-pointer group transform transition-all duration-700 ease-out ${
        isVisible 
          ? 'translate-y-0 opacity-100' 
          : 'translate-y-12 opacity-0'
      }`}
      style={{
        animationDelay: `${index * 200}ms`,
        borderRadius: '4px',
        transition: 'box-shadow 0.6s ease, transform 0.7s ease-out, opacity 0.7s ease-out',
        transitionDelay: isVisible ? `${index * 200}ms` : '0ms',
        boxShadow: hovered
          ? `0 0 18px 4px ${greenGlow}, 0 0 36px 8px ${greenGlowSoft}, inset 0 0 0 1px ${greenBorder}`
          : `0 0 0px 0px rgba(0,0,0,0), inset 0 0 0 1px ${greenBorderDim}`,
        aspectRatio: '3 / 4',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
    >
      <img
        src={category.coverImage}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        style={{
          objectPosition: category.id === 'metal' ? 'left center' : category.id === 'fashion' ? 'center 65%' : 'center top',
          transform: category.id === 'fashion' ? 'scale(1.28)' : undefined,
          transformOrigin: 'center 68%',
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-black/5 transition-opacity duration-500" />

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          transition: 'opacity 0.6s ease',
          opacity: hovered ? 1 : 0,
          background: `linear-gradient(to bottom, ${overlayColor} 0%, transparent 30%, transparent 70%, ${overlayColorBottom} 100%)`,
        }}
      />

      <div className="absolute bottom-0 left-0 right-0 px-6 pb-7 pt-10 bg-gradient-to-t from-black/60 via-black/30 to-transparent">
        <div
          className="transition-all duration-500"
          style={{
            height: '1px',
            background: `linear-gradient(to right, ${greenAccent}e6, ${greenAccent}4d, transparent)`,
            width: hovered ? '85%' : '40%',
          }}
        />
        <div
          className="flex items-center gap-2 mt-5 transition-all duration-500"
          style={{
            opacity: hovered ? 1 : 0,
            transform: hovered ? 'translateY(0)' : 'translateY(8px)',
          }}
        >
          <i className="ri-image-line text-sm" style={{ color: greenAccent }}></i>
          <span className="font-cinzel text-xs tracking-widest" style={{ color: greenAccent }}>
            VIEW GALLERY
          </span>
        </div>
      </div>
    </div>
  );
}

export default function VenturesSection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden py-16 md:py-32 px-4 md:px-6"
      style={{
        backgroundImage: 'url(https://public.readdy.ai/ai/img_res/edited_69ece5e0603a8ac09dfd4f420771de7e_afaee088.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* 顶部渐变 */}
      <div
        className="absolute top-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: '140px',
          background: 'linear-gradient(to bottom, #0a0a0a 0%, transparent 100%)',
        }}
      />
      {/* 底部渐变 */}
      <div
        className="absolute bottom-0 left-0 right-0 z-[1] pointer-events-none"
        style={{
          height: '140px',
          background: 'linear-gradient(to top, #0f0e0b 0%, transparent 100%)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className={`text-center mb-10 md:mb-16 transform transition-all duration-1000 ease-out ${
          isVisible 
            ? 'translate-y-0 opacity-100' 
            : 'translate-y-8 opacity-0'
        }`}>
          <div className="inline-flex items-center gap-2 mb-4 md:mb-6">
            <i className="ri-camera-line text-lg md:text-xl" style={{ color: '#3a7a52' }}></i>
            <span className="font-cormorant text-xs tracking-widest" style={{ color: '#3a7a52', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>PHOTO GALLERY</span>
          </div>
          <h2
            className="text-2xl md:text-4xl font-cinzel-decorative font-bold mb-4 cursor-pointer hover:opacity-80 transition-opacity duration-300"
            style={{ color: '#3a7a52', textShadow: '0 2px 10px rgba(0,0,0,0.7)' }}
            onClick={() => navigate('/gallery/ventures')}
          >
            Ventures beyond Classical
          </h2>
          <p className="font-cormorant italic text-base md:text-lg" style={{ color: '#ede8d8', opacity: 0.6, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
            Life outside the Opera lights
          </p>
        </div>

        {/* Three Cards */}
        <div className="max-w-5xl mx-auto">
          {/* 手机端：竖向一个个排列，整体缩小 */}
          <div className="flex flex-col gap-3 md:hidden px-8">
            {ventureCategories.map((cat, i) => (
              <div key={cat.id} className="w-full max-w-xs mx-auto">
                <CategoryCard category={cat} index={i} isVisible={isVisible} />
              </div>
            ))}
          </div>
          {/* 桌面端：原有横排 */}
          <div className="hidden md:flex flex-row gap-5">
            {ventureCategories.map((cat, i) => (
              <div key={cat.id} className="flex-1 min-w-0">
                <CategoryCard category={cat} index={i} isVisible={isVisible} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
